<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <h1 class="h4 mb-1">Availability</h1>
        <p class="text-muted mb-0">
          Set when each rental product is open, closed for cleaning or under maintenance, and its price for those days
        </p>
      </CCardBody>
    </CCard>

    <CCard>
      <CCardBody>
        <CRow>
          <CCol md="6" lg="4">
            <CSelect
              :value.sync="selectedProductId"
              :options="rentalProducts"
              label="Rental product"
              @change="onSelectRentalProduct"
            />
          </CCol>
          <CCol md="6" lg="8" class="d-flex justify-content-md-end align-items-end">
            <CSelect
              :options="viewOptions"
              :value.sync="displayPeriod"
              label="View"
            />
          </CCol>
        </CRow>

        <div v-if="productsError" class="text-center p-5">
          <CIcon name="cil-warning" size="xl" class="text-danger mb-2" />
          <h2 class="h6">Couldn't load your rental products</h2>
          <p class="text-muted">Check your connection and try again.</p>
          <CButton color="primary" variant="outline" @click="loadRentalProducts">
            <CIcon name="cil-reload" /> Retry
          </CButton>
        </div>
        <div v-else-if="productsLoaded && rentalProducts.length <= 1" class="text-center p-5">
          <CIcon name="cil-home" size="xl" class="text-muted mb-2" />
          <h2 class="h6">No rental products yet</h2>
          <p class="text-muted">Add a room, unit or item first, then set its availability here.</p>
          <CButton color="primary" @click="$router.push({ path: '/tenants/RentalProduct' })">
            <CIcon name="cil-plus" /> Add product
          </CButton>
        </div>
        <CRow v-else>
          <CCol lg="8" class="mb-4">
            <CAlert v-if="error" color="danger" class="d-flex justify-content-between align-items-center">
              <span>Couldn't load availability for this product.</span>
              <CButton color="danger" variant="outline" @click="refreshTable">Retry</CButton>
            </CAlert>
            <calendar-view
              id="calendar"
              :events="events"
              enable-drag-drop
              :show-date="showDate"
              :displayPeriodUom="displayPeriod"
              currentPeriodLabel="icons"
              :class="themeClasses"
              :day-classes="dayClasses"
              @click-date="onClickDay"
              @drop-on-date="onDrop"
              @show-date-change="setShowDate"
              ref="calendarView"
            >
              <calendar-view-header
                slot="header"
                slot-scope="{ headerProps }"
                :header-props="headerProps"
                @input="setShowDate"
              />
            </calendar-view>
            <small class="form-text text-muted">
              Click a day to set the start date, then another day to set the end date.
            </small>
          </CCol>
          <CCol lg="4">
            <CForm @submit.prevent="openSaveConfirm">
              <h2 class="h6">Set availability</h2>
              <CInput
                label="From *"
                type="date"
                v-model="availability.startDate"
                :is-valid="formErrors.startDate ? false : null"
                :invalid-feedback="formErrors.startDate"
              />
              <CInput
                label="To *"
                type="date"
                v-model="availability.endDate"
                :is-valid="formErrors.endDate ? false : null"
                :invalid-feedback="formErrors.endDate"
              />

              <CSelect
                :value.sync="availability.status"
                :options="statuses"
                label="Status"
              />
              <div class="mb-3">
                <CBadge :color="selectedStatusColor">{{ selectedStatusLabel }}</CBadge>
              </div>
              <CInput
                label="Price per day"
                type="number"
                inputmode="decimal"
                prepend="RM"
                v-model="availability.price"
              />

              <CButton color="primary" type="submit" :disabled="saving">
                Save availability
              </CButton>
            </CForm>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Confirm availability change (section 5.4) -->
    <CModal :show.sync="confirmSaveModal" :centered="true" :close-on-backdrop="!saving">
      <template #header>
        <h5 class="modal-title">Set {{ selectedProductName }} to {{ selectedStatusLabel }}?</h5>
      </template>
      <p class="mb-0">
        {{ selectedProductName }} will be marked {{ selectedStatusLabel.toLowerCase() }} from
        {{ formatDate(availability.startDate) }} to {{ formatDate(availability.endDate) }}
        at RM {{ formatMyr(availability.price) }} per day.
      </p>
      <template #footer>
        <CButton color="light" :disabled="saving" @click="confirmSaveModal = false">
          Cancel
        </CButton>
        <CButton color="primary" :disabled="saving" @click="saveAvailability">
          <CSpinner v-if="saving" size="sm" class="mr-1" />
          Save availability
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import RentalAvailabilityApi from "@/lib/rentalAvailabilityApi";
import {
  CalendarView,
  CalendarViewHeader,
  CalendarMathMixin,
} from "vue-simple-calendar";
// The next two lines are processed by webpack. Both CSS files are optional theme files.
import "vue-simple-calendar/static/css/default.css";
import "vue-simple-calendar/static/css/holidays-us.css";
import RentalProductApi from "@/lib/rentalProductApi";
import { asDateString, formatDate } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";

// Section 3.3 `availability` map (RentalAvailabilityStatus), keyed by backend enum name
const AVAILABILITY_STATUS = {
  Available: { color: "success", label: "Available", eventClass: "available-background" },
  Close: { color: "secondary", label: "Closed", eventClass: "closed-background" },
  Cleaning: { color: "info", label: "Cleaning", eventClass: "cleaning-background" },
  Maintenance: { color: "warning", label: "Maintenance", eventClass: "maintenance-background" },
};

const currDate = new Date();

export default {
  name: "RentalAvailability",
  mixins: [CalendarMathMixin],
  components: {
    CalendarView,
    CalendarViewHeader,
  },
  data() {
    return {
      statuses: [],
      items: [],
      availability: {
        startDate: "",
        endDate: "",
        price: 0,
        status: 0,
      },
      formErrors: {},
      selectedProductId: null,
      rentalProducts: [],
      productsLoaded: false,
      productsError: false,
      loading: false,
      error: false,
      saving: false,
      confirmSaveModal: false,
      showDate: currDate,
      displayPeriod: "month",
      viewOptions: [
        { value: "year", label: "Year" },
        { value: "month", label: "Month" },
        { value: "week", label: "Week" },
      ],
      events: [],
      useDefaultTheme: true,
      useHolidayTheme: false,
      coloredDays: {},
      api: new RentalAvailabilityApi(),
      rentalProductApi: new RentalProductApi(),
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Rental & Leasing", "Availability"]);
    this.fetchStatuses();

    this.loadRentalProducts();
    this.refreshTable();
  },
  computed: {
    selectedStatus() {
      return this.statuses.find((s) => s.value === this.availability.status);
    },
    selectedStatusColor() {
      return this.selectedStatus ? this.selectedStatus.color : "secondary";
    },
    selectedStatusLabel() {
      return this.selectedStatus ? this.selectedStatus.label : "Not set";
    },
    selectedProductName() {
      const p = this.rentalProducts.find((x) => x.value === this.selectedProductId);
      return p ? p.label : "This product";
    },
    dayClasses() {
      return (day) => {
        const dayKey = asDateString(day); // YYYY-MM-DD in Malaysia time
        return this.coloredDays[dayKey] || "";
      };
    },
    themeClasses() {
      return {
        "theme-default": this.useDefaultTheme,
        "holiday-us-traditional": this.useHolidayTheme,
        "holiday-us-official": this.useHolidayTheme,
      };
    },
  },
  methods: {
    formatDate,
    formatMyr,
    onSelectRentalProduct() {
      this.refreshTable();
    },

    fetchStatuses() {
      var self = this;
      self.api
        .getStatus()
        .then((response) => {
          this.statuses = response.result.map((state) => {
            const meta = AVAILABILITY_STATUS[state.name] || {};
            return {
              value: state.id,
              label: meta.label || state.name,
              color: meta.color || "secondary",
            };
          });
        })
        .catch((err) => {
          console.error(err);
          self.$toast.error("Couldn't load the availability statuses. Refresh the page to try again.");
        });
    },
    getEventClass(status) {
      return (AVAILABILITY_STATUS[status] || {}).eventClass || "default-background";
    },

    refreshTable() {
      var self = this;
      if (!self.selectedProductId) return;
      self.loading = true;
      self.error = false;
      self.api
        .getListByRentalProduct(self.selectedProductId)
        .then((response) => {
          self.items = response.result;
          this.events = response.result.map((a) => ({
            title: " ",
            startDate: new Date(a.startDateTime),
            endDate: new Date(a.endDateTime),
            allDay: true, // Ensures the full day is covered
            classes: this.getEventClass(a.statusDescription),
          }));
        })
        .catch((err) => {
          console.error(err);
          self.error = true;
        })
        .finally(() => {
          self.loading = false;
        });
    },

    validate() {
      const errors = {};
      if (!this.selectedProductId) {
        this.$toast.warning("Choose a rental product first");
        return false;
      }
      if (!this.availability.startDate) errors.startDate = "Choose the first day";
      if (!this.availability.endDate) errors.endDate = "Choose the last day";
      else if (this.availability.startDate && this.availability.endDate < this.availability.startDate)
        errors.endDate = "The last day must be on or after the first day";
      this.formErrors = errors;
      return Object.keys(errors).length === 0;
    },
    openSaveConfirm() {
      if (!this.validate()) return;
      this.confirmSaveModal = true;
    },
    saveAvailability() {
      const self = this;
      const data = {
        rentalProductId: this.selectedProductId,
        startDateTime: this.availability.startDate,
        endDateTime: this.availability.endDate,
        price: this.availability.price,
        status: this.availability.status,
      };
      self.saving = true;
      this.api
        .create(data)
        .then(() => {
          self.confirmSaveModal = false;
          self.refreshTable();
          self.$toast.success("Availability saved");
        })
        .catch(({ data }) => {
          self.$toast.error(
            `Couldn't save the availability. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          self.saving = false;
        });
    },
    loadRentalProducts() {
      this.productsError = false;
      this.rentalProductApi
        .getListByCurrentBusiness()
        .then((response) => {
          this.rentalProducts = [
            { value: "", label: "Choose a product", disabled: true },
            ...response.result.map((p) => ({
              value: p.id,
              label: p.name,
            })),
          ];
          this.selectedProductId = "";
          this.productsLoaded = true;
        })
        .catch((err) => {
          console.error(err);
          this.productsError = true;
        });
    },
    setShowDate(d) {
      this.showDate = d;
    },
    onClickDay(date) {
      // Calendar day as "yyyy-MM-dd" in Malaysia time (no UTC drift)
      const localDate = asDateString(date);
      this.formErrors = {};

      // If start date is not set, set it first
      if (!this.availability.startDate) {
        this.availability.startDate = localDate;
        this.events.push({
          title: "Selected",
          startDate: new Date(this.availability.startDate),
          endDate: new Date(this.availability.startDate),
          tempEvent: true, // Mark as temporary event
          classes: "selection-background",
        });
      }
      // If start date is set but end date is not, set the end date
      else if (!this.availability.endDate) {
        if (localDate >= this.availability.startDate) {
          this.availability.endDate = localDate;

          // Remove previous temporary event
          this.events = this.events.filter((event) => !event.tempEvent);

          // Add a new event to highlight selected range in the calendar
          this.events.push({
            title: "Selected",
            startDate: new Date(this.availability.startDate),
            endDate: new Date(this.availability.endDate),
            tempEvent: true, // Mark as temporary event
            classes: "selection-background",
          });
        } else {
          this.availability.startDate = localDate;
          this.availability.endDate = "";

          // Remove previous temporary event
          this.events = this.events.filter((event) => !event.tempEvent);

          this.events.push({
            title: "Selected",
            startDate: new Date(this.availability.startDate),
            endDate: new Date(this.availability.startDate),
            tempEvent: true, // Mark as temporary event
            classes: "selection-background",
          });
        }
      }
      // If both are already set, reset and start over
      else {
        this.availability.startDate = "";
        this.availability.endDate = "";

        // Remove previous temporary event
        this.events = this.events.filter((event) => !event.tempEvent);
      }
    },
    onDrop(event, date) {
      // Determine the delta between the old start date and the date chosen,
      // and apply that delta to both the start and end date to move the event.
      const eLength = this.dayDiff(event.startDate, date);
      event.originalEvent.startDate = this.addDays(event.startDate, eLength);
      event.originalEvent.endDate = this.addDays(event.endDate, eLength);
    },
  },
};
</script>

<style lang="scss">
@import "../../assets/scss/style";
#calendar {
  color: $body-color;
  height: 63vh;
  margin-left: auto;
  margin-right: auto;
}
.theme-default .cv-event {
  background-color: $gray-300;
  border-color: $table-border-color;
  color: $body-color;
}
.theme-default .cv-day.today {
  background-color: $gray-300;
}
.theme-default .cv-day.today > .cv-day-number {
  background-color: $primary;
  color: $white;
  border-radius: 50%;
  position: absolute;
  left: 0;
}
.theme-default .cv-day.past {
  background-color: $body-bg;
}
.theme-default .cv-day.outsideOfMonth {
  background-color: $body-bg;
}
.theme-default .cv-weeks,
.theme-default .cv-day {
  border-color: $table-border-color;
}
.theme-default .cv-header,
.theme-default .cv-header-days,
.theme-default .cv-header-day {
  background-color: $gray-200;
  border-color: $table-border-color;
  border-style: solid;
}
.theme-default .cv-header-nav > button {
  color: $body-color;
  background-color: $input-bg;
  border-color: $input-border-color;
}
.theme-default .cv-day.draghover {
  outline: 2px solid $primary;
  outline-offset: -2px;
}

// Availability backgrounds use the section 3.3 colours (available success, closed secondary,
// cleaning info, maintenance warning). `top` must override the library's inline style.
.theme-default .cv-event.available-background,
.theme-default .cv-event.closed-background,
.theme-default .cv-event.cleaning-background,
.theme-default .cv-event.maintenance-background,
.theme-default .cv-event.selection-background {
  height: 100%;
  top: 0 !important;
  border-radius: 0;
  pointer-events: none;
}
.theme-default .cv-event.available-background {
  background-color: rgba($success, 0.3);
}
.theme-default .cv-event.closed-background {
  background-color: rgba($secondary, 0.3);
}
.theme-default .cv-event.cleaning-background {
  background-color: rgba($info, 0.3);
}
.theme-default .cv-event.maintenance-background {
  background-color: rgba($warning, 0.3);
}
.theme-default .cv-event.selection-background {
  background-color: rgba($primary, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
